import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from "@/components/ui/button";
import { User, Menu, X } from "lucide-react";
import { useAuth } from '@/hooks/useAuth';
import ThemeToggle from '@/components/ThemeToggle';
import { Logo } from '@/components/Logo';
import { useState } from 'react';
const MarketingNav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    t
  } = useTranslation();
  const {
    user,
    loading
  } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navLinks = [{
    label: t('marketing.howItWorks'),
    href: '/#how-it-works'
  }, {
    label: t('marketing.features'),
    href: '/#features'
  }, {
    label: t('marketing.salesPrograms'),
    href: '/for-sales-programs'
  }, {
    label: t('marketing.enterprise'),
    href: '/for-enterprise'
  }, {
    label: t('marketing.pricing'),
    href: '/pricing'
  }];
  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    if (href.startsWith('/#')) {
      e.preventDefault();
      const sectionId = href.substring(2);
      if (location.pathname === '/') {
        // Already on home page, just scroll
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({
            behavior: 'smooth'
          });
        }
      } else {
        // Navigate to home first, then scroll
        navigate('/');
        setTimeout(() => {
          const element = document.getElementById(sectionId);
          if (element) {
            element.scrollIntoView({
              behavior: 'smooth'
            });
          }
        }, 100);
      }
    } else {
      // Regular page navigation - scroll to top
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  };
  return <header className="fixed top-0 left-0 right-0 z-50 animate-fade-in">
      <div className="mx-4 mt-4">
        <nav className="glass rounded-2xl px-4 sm:px-6 py-3 relative flex items-center justify-between gap-4 shadow-none">
          {/* Logo */}
          <Logo linkTo="/" />

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-6 absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
          {navLinks.map(link => <Link key={link.label} to={link.href} onClick={e => handleNavClick(e, link.href)} className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors whitespace-nowrap">
                {link.label}
              </Link>)}
          </div>


          {/* Right side */}
          <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
            <ThemeToggle />
            
            {/* Desktop Auth Buttons */}
            <div className="hidden sm:flex items-center gap-2">
              {!loading && (user ? <Button variant="glass" onClick={() => navigate('/dashboard')} size="sm">
                    <User className="w-4 h-4" />
                    {t('marketing.dashboard')}
                  </Button> : <>
                    <Button variant="ghost" onClick={() => navigate('/auth')} size="sm">
                      {t('marketing.signIn')}
                    </Button>
                    <Button variant="glass" onClick={() => navigate('/auth')} size="sm" className="bg-primary/20 hover:bg-primary/30 text-primary border-primary/30 hover:border-primary/50 shadow-lg shadow-primary/10 hover:shadow-primary/20">
                      {t('marketing.getStarted')}
                    </Button>
                  </>)}
            </div>

            {/* Mobile Menu Button */}
            <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
          </div>
        </nav>

        {/* Mobile Menu */}
        {mobileMenuOpen && <div className="lg:hidden mt-2 glass rounded-2xl p-4 animate-fade-in">
            <div className="flex flex-col gap-2">
              {navLinks.map(link => <Link key={link.label} to={link.href} onClick={e => {
            handleNavClick(e, link.href);
            setMobileMenuOpen(false);
          }} className="px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted/50 rounded-lg transition-colors">
                  {link.label}
                </Link>)}
              <div className="border-t border-border my-2" />
              {!loading && !user && <Button variant="glass" onClick={() => {
            navigate('/auth');
            setMobileMenuOpen(false);
          }} className="w-full bg-primary/20 hover:bg-primary/30 text-primary border-primary/30 hover:border-primary/50 shadow-lg shadow-primary/10 hover:shadow-primary/20">
                  {t('marketing.getStarted')}
                </Button>}
              {!loading && user && <Button variant="glass" onClick={() => {
            navigate('/dashboard');
            setMobileMenuOpen(false);
          }} className="w-full">
                  <User className="w-4 h-4" />
                  {t('marketing.dashboard')}
                </Button>}
            </div>
          </div>}
      </div>
    </header>;
};
export default MarketingNav;